<template>
  <div>
    <label class="wrapper flex flex-col items-center" :for="name" :class="{ error }">
      <input type="file" :id="name" :name="name" @change="uploadFiles" :accept="acceptedFormat" :multiple="multiple" />

      <div v-if="preview && modelValue.length > 0" class="flex flex-col items-center gap-4">
        <span class="flex gap-8">
          <iconify-icon icon="fa-solid:file-image" />
          <span class="small filename" v-if="!multiple">{{ modelValue[0].name }}</span>
        </span>
        <p class="description smaller">File size: {{ totalSize.toLocaleString() }} MB</p>
      </div>

      <div v-else class="flex flex-col items-center gap-4">
        <span class="small">Click to upload</span>
        <p class="description smaller">{{ placeholder }}</p>
      </div>
    </label>
    <transition name="scale-fade">
      <div v-if="error" class="smaller communication-danger text-field__error">
        <span>
          {{ error }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';

  export default {
    name: 'FileUpload',
    emits: ['update:modelValue', 'error'],
    props: {
      placeholder: String,
      name: { required: true, type: String },
      modelValue: { type: Array as PropType<File[]>, required: true },
      accept: { type: Array as PropType<string[]>, default: () => ['*'] },
      multiple: Boolean,
      preview: Boolean,
      maxSize: { type: Number, default: 5 },
      error: { type: String, default: () => null },
    },
    methods: {
      uploadFiles(event: any) {
        const files = this.multiple ? this.modelValue : [];

        for (let i = 0; i < event.target.files.length; i++) {
          const file = event.target.files[i];

          if (!this.accept.includes('*')) {
            const suffix = file.name.split('.').pop();
            if (!this.acceptedFormat.includes(suffix.toLowerCase())) {
              this.$emit('error', `File "${file.name}" is not accepted`);
              continue;
            }
          }

          if (file.size <= this.maxSize * 1e6) {
            files.push(file);
          } else {
            this.$emit('error', `File "${file.name} is too large"`);
          }
        }
        this.$emit('update:modelValue', files);
      },
    },
    computed: {
      acceptedFormat() {
        if (this.accept.length === 0) {
          return '*';
        }
        return this.accept.join(', ');
      },
      totalSize() {
        if (this.modelValue.length > 0) {
          let total = 0;
          for (let i = 0; i < this.modelValue.length; i++) {
            total = this.modelValue[i].size;
          }
          return total / 1e6;
        }
        return 0;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/variables.scss';

  .wrapper {
    background-color: $white;
    border-radius: 0.5rem;
    padding: 0.5625rem 1rem;
    border: 1px dashed $primary-light;
    cursor: pointer;
    max-height: fit-content;
    &.error {
      border: 1px dashed $communication-danger;
    }
    input[type='file'] {
      display: none;
    }

    .filename {
      max-width: 100%;
      overflow-wrap: anywhere;
    }
  }
</style>
